"use client";

import { useState, useEffect } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  HStack,
  Text,
  Box,
  useDisclosure,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import {
  ChevronUpIcon,
  ChevronRightIcon,
  SettingsIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useAppTheme } from "../ThemeContext.tsx";
import { useCreditsGate } from "../CreditsGateContext.tsx";
import { supabase } from "../supabase.ts";

const UserProfileDropdown = () => {
  const { theme, setTheme, resolvedTheme } = useAppTheme();
  const { isCreditsLocked } = useCreditsGate();
  const isDark = resolvedTheme === "dark";
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getUserData = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        setUserEmail(user?.email || null);
        setUserName(
          user?.user_metadata?.first_name ||
            user?.email?.split("@")[0] ||
            "User"
        );
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getUserData();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  // Helper to check if a theme option is active
  const isActive = (option: string) => theme === option;

  // Map theme values to icons and labels
  const themeMap: Record<string, { icon: string; label: string }> = {
    dark: { icon: "🌙", label: "Dark" },
    light: { icon: "☀️", label: "Light" },
    system: { icon: "💻", label: "System" },
  };

  // Get the current theme details
  const currentTheme = theme || "system";
  const currentThemeData = themeMap[currentTheme] || themeMap["system"];

  // Disclosure for the nested theme menu (to open on hover)
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box
      position="absolute"
      bottom={4}
      left={4}
      right={4}
      bg={isDark ? "gray.800" : "white"}
    >
      <Menu placement="top-start">
        <MenuButton
          as={Button}
          width="full"
          variant="ghost"
          color="gray.300"
          justifyContent="start"
          _hover={{ bg: isDark ? "whiteAlpha.100" : "gray.200" }}
          _active={{ bg: isDark ? "whiteAlpha.200" : "gray.300" }}
          p={2}
        >
          {isLoading ? (
            <HStack spacing={3} width="full" justifyContent="center">
              <Spinner size="sm" color={isDark ? "gray.400" : "gray.600"} />
            </HStack>
          ) : (
            <HStack spacing={3} width="full" overflow="hidden">
              <Box
                bg={isDark ? "gray.700" : "gray.300"}
                textColor={isDark ? "white" : "gray.800"}
                rounded="full"
                w={8}
                h={8}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                {userName?.[0]?.toUpperCase()}
              </Box>

              {/* Constrain the Box so text can truncate */}
              <Box flex={1} textAlign="left" overflow="hidden" minW={0}>
                <Text
                  isTruncated
                  fontWeight="medium"
                  fontSize="sm"
                  noOfLines={1}
                  color={isDark ? "whiteAlpha.900" : "gray.500"}
                >
                  {userName}
                </Text>
                <Text
                  isTruncated
                  fontSize="xs"
                  color={isDark ? "whiteAlpha.900" : "gray.800"}
                  noOfLines={1}
                >
                  {userEmail}
                </Text>
              </Box>

              <ChevronUpIcon />
            </HStack>
          )}
        </MenuButton>

        <MenuList bg={isDark ? "gray.800" : "white"} borderColor="gray.700">
          <Box px={4} py={2} borderBottom="1px" borderColor="gray.700">
            <HStack spacing={3} w="full" overflow="hidden">
              {/* <Box
                bg={isDark ? "gray.700" : "gray.300"}
                textColor={isDark ? "white" : "gray.800"}
                rounded="full"
                w={8}
                h={8}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                {userName?.[0]?.toUpperCase()}
              </Box> */}
              <Text
                fontWeight="medium"
                fontSize="sm"
                color={isDark ? "whiteAlpha.900" : "gray.800"}
              >
                {userEmail}
              </Text>
            </HStack>
          </Box>

          <Tooltip
            label="You have less than 1 credit remaining. Open Billing to purchase credits or manage your subscription before using account preferences."
            placement="left"
            openDelay={200}
            maxW="260px"
            hasArrow
            isDisabled={!isCreditsLocked}
            shouldWrapChildren
          >
            <MenuItem
              isDisabled={isCreditsLocked}
              onClick={() => {
                if (isCreditsLocked) return;
                navigate("/account");
              }}
              icon={<SettingsIcon />}
              bg="transparent"
              _hover={{ bg: isDark ? "whiteAlpha.100" : "gray.200" }}
              color={isDark ? "whiteAlpha.900" : "gray.800"}
              fontSize="sm"
            >
              Account preferences
            </MenuItem>
          </Tooltip>

          {/* Nested menu for theme options */}
          <Menu placement="right-start" isOpen={isOpen}>
            {/* Use MenuButton as a MenuItem that triggers the nested menu on hover */}
            <MenuButton
              as={MenuItem}
              onClick={onOpen}
              bg="transparent"
              _hover={{ bg: isDark ? "whiteAlpha.100" : "gray.200" }}
              fontSize="xs"
              color={isDark ? "whiteAlpha.900" : "gray.800"}
              justifyContent="space-between"
            >
              <HStack justifyContent="space-between" width="100%">
                <Text fontWeight="medium">
                  {currentThemeData.icon} {currentThemeData.label}
                </Text>
                <ChevronRightIcon boxSize={4} />
              </HStack>
            </MenuButton>
            <MenuList
              onMouseEnter={onOpen}
              onMouseLeave={onClose}
              bg={isDark ? "gray.800" : "white"}
              borderColor="gray.700"
            >
              <MenuItem
                onClick={() => setTheme("dark")}
                bg={
                  isActive("dark")
                    ? isDark
                      ? "gray.900"
                      : "gray.100"
                    : "transparent"
                }
                _hover={{ bg: isDark ? "whiteAlpha.100" : "gray.200" }}
                fontSize="sm"
                icon={<Box w={4}>🌙</Box>}
                color={isDark ? "whiteAlpha.900" : "gray.800"}
              >
                Dark
              </MenuItem>
              <MenuItem
                onClick={() => setTheme("light")}
                bg={
                  isActive("light")
                    ? isDark
                      ? "gray.900"
                      : "gray.100"
                    : "transparent"
                }
                _hover={{ bg: isDark ? "whiteAlpha.100" : "gray.200" }}
                fontSize="sm"
                icon={<Box w={4}>☀️</Box>}
                color={isDark ? "whiteAlpha.900" : "gray.800"}
              >
                Light
              </MenuItem>
              <MenuItem
                onClick={() => setTheme("system")}
                bg={
                  isActive("system")
                    ? isDark
                      ? "gray.900"
                      : "gray.100"
                    : "transparent"
                }
                _hover={{ bg: isDark ? "whiteAlpha.100" : "gray.200" }}
                fontSize="sm"
                icon={<Box w={4}>💻</Box>}
                color={isDark ? "whiteAlpha.900" : "gray.800"}
              >
                System
              </MenuItem>
            </MenuList>
          </Menu>

          <MenuDivider borderColor="gray.700" />
          <MenuItem
            onClick={handleLogout}
            bg="transparent"
            _hover={{ bg: isDark ? "whiteAlpha.100" : "gray.200" }}
            fontSize="sm"
            color="red.400"
          >
            Log out
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};

export default UserProfileDropdown;
